import { getCookie } from '@utiljs/cookie';
import { getEnv } from './getEnv';
import { getWmPoiId, getAcctId } from './ids';
import { reportCustomError } from './owlReporter';

interface ICustomData {
    [index: string]: any;
}

interface IExtraEnvironment {
    [index: string]: string | number;
}

// 设置默认环境变量
try {
    window.LXAnalytics('set', {
        cityid: getCookie('city_id'),
        uid: getAcctId(),
        poi_id: getWmPoiId(), // eslint-disable-line
    });
} catch (e) {
    reportCustomError({
        name: 'set变量失败',
        message: `${e && e.stack}`,
        category: 'LX_ERROR',
        level: 'WARN',
    });
}

/**
 * 上报 PV
 *
 * @param cid 页面ID
 * @param customData (可选) 业务自定义数据
 * @param extraEnvironment (可选) 额外的环境变量
 */
const reportPV = (
    cid: string,
    customData: ICustomData = {},
    extraEnvironment: IExtraEnvironment = {},
) => {
    const valLab = {
        poi_id: getWmPoiId(), //eslint-disable-line
        custom: { _page_env: getEnv(), _asset_version: window.STATIC_ASSET_VERSION, ...customData }, //eslint-disable-line
    };

    try {
        window.LXAnalytics('pageView', valLab, extraEnvironment, cid);
    } catch (e) {
        console.log('LX PV异常：', e); // eslint-disable-line
        reportCustomError({
            name: '上报pageView失败',
            message: `${e && e.stack}`,
            category: 'LX_ERROR',
            level: 'WARN',
        });
    }
};

/**
 * 上报 PD
 *
 * @param cid 页面ID
 * @param duration 停留时间，单位: ms
 * @param customData (可选) 业务自定义数据
 */
const reportPD = (cid: string, duration: number, customData: ICustomData = {}) => {
    const valLab = {
        poi_id: getWmPoiId(), //eslint-disable-line
        duration,
        custom: { _page_env: getEnv(), ...customData }, //eslint-disable-line
    };

    try {
        window.LXAnalytics('pageDisappear', valLab, { cid });
    } catch (e) {
        console.log('LX PD异常：', e); //eslint-disable-line
        reportCustomError({
            name: '上报pageDisappear失败',
            message: `${e && e.stack}`,
            category: 'LX_ERROR',
            level: 'WARN',
        });
    }
};

interface IMVExtraOptions {
    /**
     * 指定本次埋点上报的延迟时间,单位是秒。在此时间内发送的带有delay参数的其它MV事件，都会被合并入缓存，并在该时间后合并上报。发送多个delay值不同的MV时，会在最小的delay值后发送，并且不会导致数据重复上报。
     *
     * *注意：一次合并发送多条数据时，可能会超过get长度限制，进而转用post方式上报，而post方式上报的成功率在弱网环境下偏低，请谨慎使用。*
     */
    delay?: number;
}
/* jscpd:ignore-start */
/**
 * 上报 MV
 *
 * @param bid 模块ID
 * @param cid 页面ID
 * @param customData (可选) 业务自定义数据
 * @param extraOptions (可选) 额外参数
 */
const reportMV = (
    bid: string,
    cid: string,
    customData: ICustomData = {},
    extraOptions: IMVExtraOptions = {},
) => {
    const valLab = {
        poi_id: getWmPoiId(), //eslint-disable-line
        custom: { _page_env: getEnv() }, //eslint-disable-line
        // 模块相关事件业务扩展字段与 custom 平级
        // 埋点规范：https://km.sankuai.com/page/37001569#id-4.2%E4%B8%9A%E5%8A%A1%E6%89%A9%E5%B1%95%E5%AD%97%E6%AE%B5%EF%BC%9A
        ...customData,
    };

    const options = { cid, ...extraOptions };
    try {
        window.LXAnalytics('moduleView', bid, valLab, options);
    } catch (e) {
        console.log('LX MV异常：', e); //eslint-disable-line
        reportCustomError({
            name: '上报moduleView失败',
            message: `${e && e.stack}`,
            category: 'LX_ERROR',
            level: 'WARN',
        });
    }
};

interface IMCExtraOptions {
    /**
     * isLeave 表示点击事件埋点选项是否页面离开点击事件，如果为 true 则会在MC事件后追加页面离开事件
     */
    isLeave?: boolean;
}

/**
 * 上报 MC
 *
 * @param bid 模块ID
 * @param cid 页面ID
 * @param customData (可选) 业务自定义数据
 * @param extraOptions (可选) 额外参数
 */
const reportMC = (
    bid: string,
    cid: string,
    customData: ICustomData = {},
    extraOptions: IMCExtraOptions = {},
) => {
    const valLab = {
        poi_id: getWmPoiId(), //eslint-disable-line
        custom: { _page_env: getEnv() }, //eslint-disable-line
        // 模块相关事件业务扩展字段与 custom 平级
        // 埋点规范：https://km.sankuai.com/page/37001569#id-4.2%E4%B8%9A%E5%8A%A1%E6%89%A9%E5%B1%95%E5%AD%97%E6%AE%B5%EF%BC%9A
        ...customData,
    };

    const options = { cid, ...extraOptions };
    try {
        window.LXAnalytics('moduleClick', bid, valLab, options);
    } catch (e) {
        console.log('LX MC异常：', e); //eslint-disable-line
        reportCustomError({
            name: '上报moduleClick失败',
            message: `${e && e.stack}`,
            category: 'LX_ERROR',
            level: 'WARN',
        });
    }
};
/* jscpd:ignore-end */

export { reportPV, reportPD, reportMV, reportMC };
